import { apiCall } from '../../services/apiUtil';
import { Capacitor } from '@capacitor/core';

const platform = Capacitor.isNativePlatform() ? 'mobile' : 'web';

/**
 * Authentication API
 *
 * Handles user authentication operations:
 * - User registration (signup)
 * - User login (signin)
 * - Password change
 */
export const authAPI = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} API response
   */
  register: async (userData) => {
    const payload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber || '',
      password1: userData.password,
      password2: userData.password,
      password3: userData.password,
      securityQuestion1: userData.securityQuestion1,
      securityQuestion2: userData.securityQuestion2,
      securityQuestion3: userData.securityQuestion3,
      securityAnswer1: userData.securityAnswer1,
      securityAnswer2: userData.securityAnswer2,
      securityAnswer3: userData.securityAnswer3,
      addressId: userData.addressId || 46,
    };

    return apiCall('/user', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} API response with auth token
   */
  login: async (email, password, captchaAnswer) => {
    const body = { email, password, platform };
    if (captchaAnswer !== undefined) body.captchaAnswer = captchaAnswer;
    return apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Refresh the current session — reissues a fresh-expiry token as long as
   * the existing one is still valid. Called opportunistically while the
   * token is nearing expiry during active use (see apiUtil.js).
   * @returns {Promise} API response with a new token
   */
  refresh: async () => {
    return apiCall('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ platform }),
    });
  },

  /**
   * Change user password
   * @param {number} userId - User ID
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} API response
   */
  checkEmail: async (email) => {
    return apiCall('/auth/check-email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  googleLogin: async (credential, mode = 'signin') => {
    return apiCall('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential, mode, platform }),
    });
  },

  facebookLogin: async (accessToken, authenticationToken, mode = 'signin') => {
    return apiCall('/auth/facebook', {
      method: 'POST',
      body: JSON.stringify({ accessToken, authenticationToken, mode, platform }),
    });
  },

  appleLogin: async (identityToken, firstName, lastName) => {
    return apiCall('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ identityToken, firstName, lastName, platform: 'mobile' }),
    });
  },

  changePassword: async (userId, currentPassword, newPassword) => {
    return apiCall('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        currentPassword,
        newPassword,
      }),
    });
  },

  /**
   * Request password reset (forgot password)
   * @param {string} email - User email
   * @returns {Promise} API response
   */
  forgotPassword: async (email) => {
    return apiCall('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise} API response
   */
  resetPassword: async (token, newPassword) => {
    return apiCall('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  },
};

export default authAPI;
